import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["defaultValue", "defaultActiveValue", "onOptionFocus", "onChange"];
import React from 'react';
import PropTypes from 'prop-types';
import ControlledListbox from './ControlledListbox';
import { INPUT_SIZES } from '../../constants/INPUT';
import { CALLBACK_FREQUENCY } from '../../constants/CALLBACK_FREQUENCY';
/**
 * A listbox is a control that allows you to select one or multiple values from a list of options.
 * It has a similar purpose to a [`Combobox`](#/Form%20components?id=combobox) or
 * [`DropdownSelect`](#/Form%20components?id=dropdownselect) with the difference that it
 * doesn't conceal the options behind a dialog. You can read more about
 * the differences, pros/cons, and usage guidelines in this useful
 * [article from the Nielsen Norman Group](https://www.nngroup.com/articles/listbox-dropdown/).
 *
 * Listboxes use typeahead to quickly navigate to options. Typing characters in quick succession
 * will move the focus to the next option that matches the typed character(s).
 *
 * @since 8.28.0
 */

var Listbox = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var defaultValue = props.defaultValue,
      defaultActiveValue = props.defaultActiveValue,
      onOptionFocus = props.onOptionFocus,
      onChange = props.onChange,
      rest = _objectWithoutProperties(props, _excluded);

  var _React$useState = React.useState(defaultValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1];

  var _React$useState3 = React.useState(defaultActiveValue),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      activeValue = _React$useState4[0],
      setActiveValue = _React$useState4[1];

  var onOptionFocusCallback = React.useCallback(function (option, meta) {
    setActiveValue(option && option.value);

    if (typeof onOptionFocus === 'function') {
      onOptionFocus(option, meta);
    }
  }, [onOptionFocus]);
  var onChangeCallback = React.useCallback(function (nextValue, meta) {
    setValue(nextValue);

    if (typeof onChange === 'function') {
      onChange(nextValue, meta);
    }
  }, [onChange]);
  return /*#__PURE__*/React.createElement(ControlledListbox, _extends({
    activeValue: activeValue,
    onOptionFocus: onOptionFocusCallback,
    onChange: onChangeCallback,
    ref: ref,
    value: value
  }, rest));
});
Listbox.displayName = 'Listbox';
Listbox.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The value that currently has focus when used as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * **Note:** When `selectOnFocus` is enabled, the active option will automatically be
   * set to the selected value if this is omitted.
   */
  activeValue: PropTypes.string,

  /**
   * An optional appearance for listbox options. This will default to `'checkbox'` for
   * multiselectable lists. Icons will take precedence over this when defined on individual list
   * options.
   */
  appearance: PropTypes.oneOf(['checkbox', 'radio']),

  /**
   * The `activeValue` when used as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  defaultActiveValue: PropTypes.string,

  /**
   * The selected option(s) when used as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * This should be a string for a single-select listbox, and an array of strings for a
   * multiselectable listbox.
   */
  defaultValue: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),

  /**
   * Options will be focused automatically when they are hovered.
   *
   * Note: This should be avoided when using `selectOnFocus`.
   */
  focusOnHover: PropTypes.bool,

  /**
   * A function that maps the values passed to `options` to an object of props that will be
   * passed to `renderOption`.
   *
   * By default, `mapOptionsToProps` will convert strings into an object where the string is both
   * the `label` and `value`. If `label` or `value` is omitted from an object, the other will be
   * used. If no `id` is provided, one will automatically be generated.
   */
  mapOptionToProps: PropTypes.func,

  /**
   * Called whenever the selected value(s) changes with the new selected value(s) and a `meta`
   * object as parameters.
   *
   * - `value` will be the selected option value or values when multiselectable
   * - `meta` will have `meta.reason` detailing why the change occurred and `meta.option` as the
   *     last `option` object to change
   *
   * Note: This differs from `onOptionSelect` in that it only returns the value(s) and not the
   * option object(s).
   */
  onChange: PropTypes.func,

  /**
   * Called whenever an option is focused with `option` and `meta` objects as parameters.
   *
   * - `option` will be the object returned from `mapOptionToProps`
   * - `meta` will have a `meta.reason` detailing why the change occurred
   */
  onOptionFocus: PropTypes.func,

  /**
   * Called whenever an option is selected with `option` and `meta` objects as parameters.
   *
   * - `option` will be the object returned from `mapOptionToProps`
   * - `meta` will have a `meta.reason` detailing why the change occurred
   *
   * By default, this will only be called when the action results in a change of value.
   * You can use `selectCallbackFrequency` to change this behavior to always trigger.
   */
  onOptionSelect: PropTypes.func,

  /**
   * An array of options, or an array of optgroups. An optgroup is an array of options, where the
   * first option is the header for the group.
   *
   * Option values will be passed to `mapOptionToProps` before being passed to the `renderOption`
   * callback.
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  options: PropTypes.array,

  /**
   * A function that receives `props` as the only argument and returns an option group component,
   * by default a `ListboxGroup`.
   */
  renderGroup: PropTypes.func,

  /**
   * A function that receives `props` as the only argument and returns an option group label
   * component, by default a [`ListboxGroupLabel`](#/Form%20components?id=listboxgrouplabel).
   */
  renderGroupLabel: PropTypes.func,

  /**
   * A function that receives `props` as the only argument and returns an option component, by
   * default a [`ListboxOption`](#/Form%20components?id=listboxoption).
   */
  renderOption: PropTypes.func,

  /**
   * The ARIA role of the component. This should not be changed from the default
   * [`listbox`](https://www.w3.org/TR/wai-aria-1.2/#listbox).
   */
  role: PropTypes.string,

  /**
   * By default, `onOptionSelect` will only fire if the action results in a change of value.
   * Alternatively, you can set this to `CALLBACK_FREQUENCY.ALWAYS` and the callback will
   * always fire regardless of the outcome on the value.
   *
   * Note: When `CALLBACK_FREQUENCY.ALWAYS` is enabled, you can check if the value will
   * change by looking at the `onOptionSelect` `meta.valueChanged` property.
   */
  selectCallbackFrequency: PropTypes.oneOf(Object.values(CALLBACK_FREQUENCY)),

  /**
   * When true, focus will also trigger a select. This will always be `false` for a
   * multiselectable Listbox.
   *
   * This model of selection is known as "selection follows focus". Having selection follow focus
   * can be very helpful in some circumstances and can severely degrade accessibility in others.
   * For additional guidance, see
   * [Deciding When to Make Selection Automatically Follow Focus](https://www.w3.org/TR/wai-aria-practices-1.2/#kbd_selection_follows_focus).
   */
  selectOnFocus: PropTypes.bool,

  /**
   * By default, only the space key will toggle selection. Set this to `true` to allow the enter
   * key to select.
   */
  selectWithEnter: PropTypes.bool,

  /**
   * Space key will toggle selection by default. Set this to `false` to disable.
   */
  selectWithSpace: PropTypes.bool,

  /**
   * Listboxes have a fixed height by default. You can set this to `"fluid"` to match the height
   * of the parent container.
   */
  size: PropTypes.oneOf([INPUT_SIZES.md, 'fluid']),

  /**
   * Typing characters in quick succession will move the focus to the next option that matches the
   * typed character(s).
   *
   * This is the delay in milliseconds between keystrokes after which the typeahead input will
   * reset and start fousing a different option.
   */
  typeaheadInputDelay: PropTypes.number,

  /**
   * The selected option(s) when used as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * This should be a string for a single-select listbox, and an array of strings for a
   * multiselectable listbox.
   */
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
} : {};
Listbox.defaultProps = {
  defaultValue: ''
};
export default Listbox;